import { useSyncExternalStore } from "react";

/**
 * Whether the primary pointer is a fine one: a mouse or a trackpad rather than a finger.
 *
 * Asked for one decision, Mattej's through the planning chat on 2026-10-07 (build round 16): in the
 * client's Chatt, Enter sends on a computer and starts a new line on a phone. Whether a hardware
 * keyboard is attached is not something a page can know, so the primary pointer stands in for it. A
 * finger is a phone or a tablet, where the on-screen keyboard's return key is how a second line is
 * written and the send button is right beside the box. A mouse or a trackpad is a computer, where
 * Enter sending is what every other chat has taught.
 *
 * `useSyncExternalStore` for the same reason `useConnectionStatus` in `./connection` uses it: the
 * answer lives outside React and changes without React being told. It can change while the chat is
 * open: a tablet given a trackpad may start calling it the primary pointer, and Chrome's device
 * toolbar flips the answer the moment touch is switched on or off.
 *
 * Only a clear yes counts. A runtime without `matchMedia` (jsdom, unless a test stubs it), a query
 * that throws, and a browser that reports no pointer at all (`pointer: none`) all answer false, so
 * Enter keeps writing a new line wherever the answer is not known. Headless Chromium on Linux is one
 * of those: it reported `pointer: none` on 2026-10-07, and `tests/e2e/chat-enter-sends.spec.ts` says
 * what that means for the browser suite.
 */
const FINE_POINTER = "(pointer: fine)";

function fineQuery(): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }

  try {
    return window.matchMedia(FINE_POINTER);
  } catch {
    return null;
  }
}

function subscribeToPointer(onChange: () => void): () => void {
  const query = fineQuery();
  if (!query) {
    return () => undefined;
  }

  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function primaryPointerIsFine(): boolean {
  return fineQuery()?.matches === true;
}

/**
 * The answer as state. The server snapshot is false: nothing here is server rendered, and a page
 * with no pointer to ask about is a page where Enter should not send.
 */
export function useFinePointer(): boolean {
  return useSyncExternalStore(subscribeToPointer, primaryPointerIsFine, () => false);
}
